/**
 * Группы-массивы Автокрафта: блок полей, наборов которого может быть несколько.
 *
 * В данных макета набор — это индекс в ключе: `stop1[0]`, `stop1[1]`, у вложенного массива —
 * `stop1[0][1]` (внешний индекс, затем внутренний). Вариант набора — `{группа}.__variant[0]`,
 * видимость поля в наборе — `@stop1[0]`. Отдельного «числа наборов» в данных нет: Автокрафт
 * считает его по индексам, и форма делает так же — по ключам, которые лежат в данных. Поэтому
 * новый набор сразу получает все свои ключи (значения по умолчанию), даже пустые: иначе
 * добавленный, но ещё не заполненный набор исчезал бы из формы.
 *
 * Тело запроса поверх этого строит `autocraftPayload.ts`.
 */
import {
  AUTOCRAFT_ARRAY_VARIANT_SUFFIX,
  AUTOCRAFT_MAX_ARRAY_SETS,
  AUTOCRAFT_MAX_NESTING_DEPTH,
  type AutocraftArrayVariant,
  type AutocraftDataField,
  type AutocraftGeneratorSchema,
} from '../types/autocraft.js';
import { getAutocraftField as fieldOf, type AutocraftFormValue } from './autocraftFields.js';

type AutocraftValues = Record<string, AutocraftFormValue>;

function schemaKeys(schema: AutocraftGeneratorSchema): string[] {
  return Object.keys(schema.dataModel).filter((key) => fieldOf(schema, key) !== null);
}

/** `stop1` + `[0, 2]` → `stop1[0][2]`; без индексов — ключ как есть. */
export function autocraftIndexedKey(base: string, indices: readonly number[]): string {
  return indices.length === 0 ? base : `${base}${indices.map((index) => `[${index}]`).join('')}`;
}

/** Переводы строк: `.` прежней регулярки их не брал, и база с ними ключом набора не считалась. */
const LINE_TERMINATOR = /[\n\r\u2028\u2029]/;

function isAsciiDigit(code: number): boolean {
  return code >= 48 && code <= 57;
}

/**
 * Обратная к {@link autocraftIndexedKey}: `stop1[0][2]` → `{ base: 'stop1', indices: [0, 2] }`.
 *
 * Разбор с конца за один проход, а не регуляркой `^(.+?)((?:\[\d+\])+)$`: ленивая база перебирала
 * хвост `[0][0]…` с каждой позиции, и длинный ключ из данных точки (их пишет редактор) стоил
 * квадрата времени при каждом обновлении макетов. Результат — как у регулярки: база не пустая,
 * без переводов строк, индексы — все `[цифры]` в конце ключа.
 */
export function parseAutocraftIndexedKey(key: string): { base: string; indices: number[] } {
  const indices: number[] = [];
  let end = key.length;
  while (end > 0 && key[end - 1] === ']') {
    let digits = end - 1;
    while (digits > 0 && isAsciiDigit(key.charCodeAt(digits - 1))) {
      digits -= 1;
    }
    const open = digits - 1;
    // Не `[цифры]`, либо набор в самом начале: база не бывает пустой, он — её часть.
    if (digits === end - 1 || open <= 0 || key[open] !== '[') {
      break;
    }
    indices.push(Number(key.slice(digits, end - 1)));
    end = open;
  }
  const base = key.slice(0, end);
  if (indices.length === 0 || LINE_TERMINATOR.test(base)) {
    return { base: key, indices: [] };
  }
  return { base, indices: indices.reverse() };
}

/** Ключ варианта набора: `{группа}.__variant[…индексы предков, индекс набора]`. */
export function autocraftArrayVariantKey(groupKey: string, setIndices: readonly number[]): string {
  return autocraftIndexedKey(`${groupKey}${AUTOCRAFT_ARRAY_VARIANT_SUFFIX}`, setIndices);
}

/**
 * База ключа поля в данных: у прототипа внутри массива — `arraySourceHash` (так его ждёт
 * Автокрафт), у остальных — ключ схемы.
 */
export function autocraftFieldDataBase(key: string, field: AutocraftDataField): string {
  return field.isArrayPrototypeField && field.arraySourceHash ? field.arraySourceHash : key;
}

/**
 * Группы-массивы, внутри которых лежит поле, от внешней к внутренней. Сама группа в свою
 * цепочку не входит: у группы-массива цепочка — это массивы вокруг неё. Предки выше
 * {@link AUTOCRAFT_MAX_NESTING_DEPTH} не читаются: схему присылает чужой сервер.
 */
export function getAutocraftArrayChain(schema: AutocraftGeneratorSchema, key: string): string[] {
  const field = fieldOf(schema, key);
  if (!field) {
    return [];
  }
  const chain: string[] = [];
  const seen = new Set([key]);
  let parent = field.parent ?? null;
  for (let depth = 0; parent && !seen.has(parent) && depth < AUTOCRAFT_MAX_NESTING_DEPTH; depth += 1) {
    seen.add(parent);
    const parentField = fieldOf(schema, parent);
    if (!parentField) {
      break;
    }
    if (parentField.isArrayGroup) {
      chain.unshift(parent);
    }
    parent = parentField.parent ?? null;
  }
  // Прототип знает свою группу и без цепочки родителей — если `parent` не дошёл до неё.
  const own = field.isArrayPrototypeField ? field.arrayGroupHash : undefined;
  if (own && !chain.includes(own) && fieldOf(schema, own)?.isArrayGroup) {
    chain.push(own);
  }
  return chain;
}

/** Элемент шаблона Автокрафта — ровно то, что нужно для вложенности наборов. */
export interface AutocraftTemplateTreeNode {
  parent: string | null;
  /** Включённая группа-массив (`template.arrayGroup.enabled`). */
  isArrayGroup: boolean;
}

/**
 * Достраивает группы-массивы, которых нет в схеме формы. Автокрафт кладёт группу в `dataModel`,
 * только если она публичная или скрываемая; поля её набора при этом в схеме есть и ссылаются на
 * неё (`arrayGroupHash`), а их родитель — промежуточная группа шаблона, которой в схеме тоже нет.
 * Без группы поле читалось как обычное: форма не показывала наборов, в Автокрафт уходил ключ без
 * индекса, а колонки `Name[0]`, `@U1[0]` таблицы данных отбрасывались как лишний индекс (02.10.26).
 *
 * По одной схеме не видно, что набор лежит внутри другого набора: `parent` поля — промежуточная
 * группа шаблона («Станция», «Строка»), которой в схеме нет, и цепочка наборов на ней рвётся.
 * Колонки `Название[0][1]`, `Пересадка[0][1][0]` тогда отбрасывались, а форма показывала
 * вложенные наборы плоско в корне (02.10.26, генератор «Список на конечной станции»). Дерево
 * элементов шаблона (`tree`, с публичной страницы генератора) это чинит, как и у самого
 * Автокрафта (`collectArrayGroupAncestorHashes`): полю с потерянным родителем — ближайший набор
 * над ним, и так до корня; набор, которого нет в схеме, достраивается.
 *
 * Группа встаёт в форму перед первым своим полем, поля с потерянным родителем подвешиваются к
 * ней. Название и варианты досыпает сервер с публичной страницы генератора.
 */
export function withImpliedAutocraftArrayGroups(
  schema: AutocraftGeneratorSchema,
  tree?: ReadonlyMap<string, AutocraftTemplateTreeNode>,
): AutocraftGeneratorSchema {
  const dataModel = { ...schema.dataModel };
  const draft = { ...schema, dataModel };
  const fieldIn = (key: string | null | undefined) => (key ? fieldOf(draft, key) : null);
  const membersByGroup = new Map<string, string[]>();
  const addMember = (groupKey: string, key: string) => {
    const members = membersByGroup.get(groupKey);
    if (members) {
      members.push(key);
    } else {
      membersByGroup.set(groupKey, [key]);
    }
  };
  const implyGroup = (groupKey: string) => {
    dataModel[groupKey] = {
      type: 'group',
      isPublic: false,
      isDeletable: false,
      elementHash: groupKey,
      elementType: 'GROUP',
      parent: null,
      isArrayGroup: true,
      implied: true,
    };
  };

  for (const key of schemaKeys(schema)) {
    const group = fieldOf(schema, key)!.arrayGroupHash;
    if (group && group !== key && !fieldOf(schema, group)) {
      addMember(group, key);
    }
  }
  for (const [groupKey, members] of membersByGroup) {
    implyGroup(groupKey);
    for (const key of members) {
      const field = fieldOf(schema, key)!;
      if (!fieldIn(field.parent)) {
        dataModel[key] = { ...field, parent: groupKey };
      }
    }
  }

  let changed = membersByGroup.size > 0;
  if (tree) {
    const keyByElement = new Map<string, string>();
    for (const key of Object.keys(dataModel)) {
      const field = fieldIn(key);
      if (field) {
        keyByElement.set(field.elementHash ?? key, key);
      }
    }
    /**
     * Ближайший набор над полем по дереву шаблона. Обычные группы схемы по пути пропускаем: их
     * раскладку в форме не трогаем — нужна только цепочка наборов. Подъём — не выше
     * {@link AUTOCRAFT_MAX_NESTING_DEPTH} групп от поля, считая и подъём достроенных наборов.
     */
    const attach = (key: string, budget = AUTOCRAFT_MAX_NESTING_DEPTH) => {
      const field = fieldIn(key)!;
      const seen = new Set<string>();
      let hash = tree.get(field.elementHash ?? key)?.parent ?? field.parent ?? null;
      let left = budget;
      while (hash && !seen.has(hash) && left > 0) {
        left -= 1;
        seen.add(hash);
        const node = tree.get(hash);
        if (!node) {
          return;
        }
        if (node.isArrayGroup) {
          let groupKey = keyByElement.get(hash);
          if (!groupKey) {
            groupKey = hash;
            implyGroup(groupKey);
            keyByElement.set(hash, groupKey);
            attach(groupKey, left);
          }
          if (groupKey === key) {
            return;
          }
          dataModel[key] = { ...fieldIn(key)!, parent: groupKey };
          addMember(groupKey, key);
          changed = true;
          return;
        }
        hash = node.parent;
      }
    };
    for (const key of Object.keys(dataModel)) {
      const field = fieldIn(key);
      if (field && !fieldIn(field.parent)) {
        attach(key);
      }
    }
  }
  if (!changed) {
    return schema;
  }

  // Внутренний набор ставим раньше внешнего: внешний встаёт перед ним, а не в конец формы.
  const depth = (groupKey: string) => getAutocraftArrayChain(draft, groupKey).length;
  const newGroups = [...membersByGroup.keys()]
    .filter((groupKey) => !fieldOf(schema, groupKey))
    .sort((a, b) => depth(b) - depth(a));

  /** Группа — перед первым своим полем: там, где Автокрафт показывает набор. */
  const insertGroups = <T>(list: T[], keyOf: (item: T) => string | undefined, make: (group: string) => T) => {
    const next = [...list];
    for (const groupKey of newGroups) {
      const members = new Set(membersByGroup.get(groupKey));
      const at = next.findIndex((item) => {
        const key = keyOf(item);
        return !!key && members.has(key);
      });
      next.splice(at >= 0 ? at : next.length, 0, make(groupKey));
    }
    return next;
  };
  if (schema.dataModel._order) {
    dataModel._order = insertGroups(schema.dataModel._order, (key) => key, (group) => group);
  }
  const items = schema.dataForm?.items;
  return {
    ...schema,
    dataModel,
    ...(schema.dataForm && items
      ? {
          dataForm: {
            ...schema.dataForm,
            items: insertGroups(
              items,
              (item) => (item.type === 'field' ? item.fieldKey : undefined),
              (group) => ({ id: `field:${group}`, type: 'field', fieldKey: group, width: 'full' }),
            ),
          },
        }
      : {}),
  };
}

/** Поле повторяется по наборам (лежит внутри группы-массива). */
export function isAutocraftArrayField(schema: AutocraftGeneratorSchema, key: string): boolean {
  return getAutocraftArrayChain(schema, key).length > 0;
}

export function autocraftSchemaHasArrays(schema: AutocraftGeneratorSchema): boolean {
  return schemaKeys(schema).some((key) => fieldOf(schema, key)?.isArrayGroup === true);
}

/** Варианты набора группы-массива (пустой список — выбирать нечего). */
export function getAutocraftArrayVariants(field: AutocraftDataField): AutocraftArrayVariant[] {
  if (!Array.isArray(field.arrayVariants)) {
    return [];
  }
  return field.arrayVariants.filter(
    (item): item is AutocraftArrayVariant =>
      !!item && typeof item === 'object' && typeof item.id === 'string' && item.id !== '',
  );
}

/**
 * Чья цепочка массивов у каждой базы ключа данных: у поля — его массивы, у варианта набора
 * группы — массивы вокруг группы плюс она сама (индекс варианта — это индекс набора).
 */
const ownerChainsCache = new WeakMap<AutocraftGeneratorSchema, Map<string, string[]>>();

function ownerChains(schema: AutocraftGeneratorSchema): Map<string, string[]> {
  const cached = ownerChainsCache.get(schema);
  if (cached) {
    return cached;
  }
  const map = new Map<string, string[]>();
  for (const key of schemaKeys(schema)) {
    const field = fieldOf(schema, key)!;
    if (field.type === 'link') {
      continue;
    }
    const chain = getAutocraftArrayChain(schema, key);
    map.set(autocraftFieldDataBase(key, field), chain);
    if (field.isArrayGroup) {
      map.set(`${key}${AUTOCRAFT_ARRAY_VARIANT_SUFFIX}`, [...chain, key]);
    }
  }
  ownerChainsCache.set(schema, map);
  return map;
}

interface ParsedValueKey {
  key: string;
  visibility: boolean;
  base: string;
  indices: number[];
  chain: string[];
}

/**
 * Ключи данных, которые относятся к полям схемы и несут правильное число индексов. Индекс за
 * {@link AUTOCRAFT_MAX_ARRAY_SETS} — не набор: по нему считается длина массива наборов.
 */
function parseValueKeys(schema: AutocraftGeneratorSchema, values: Record<string, unknown>): ParsedValueKey[] {
  const chains = ownerChains(schema);
  const result: ParsedValueKey[] = [];
  for (const key of Object.keys(values)) {
    const visibility = key.startsWith('@');
    const { base, indices } = parseAutocraftIndexedKey(visibility ? key.slice(1) : key);
    const chain = chains.get(base);
    if (
      !chain ||
      chain.length !== indices.length ||
      indices.some((index) => index >= AUTOCRAFT_MAX_ARRAY_SETS)
    ) {
      continue;
    }
    result.push({ key, visibility, base, indices, chain });
  }
  return result;
}

/**
 * Ключи данных с недостающими внешними индексами → полные: `name` → `name[0]`,
 * `Пересадка[2]` → `Пересадка[0][0][2]`. Так лежат данные точек, собранных, пока схема не знала
 * всей вложенности (группы не было в схеме, либо цепочка рвалась на промежуточной группе
 * шаблона): цепочка тогда была хвостом настоящей, а наружные наборы — первыми. Ключ, который уже
 * лежит в полном виде, старше: его не перетираем.
 */
export function withAutocraftLegacyArrayKeys(
  schema: AutocraftGeneratorSchema,
  saved: Record<string, unknown>,
): Record<string, unknown> {
  const chains = ownerChains(schema);
  let result: Record<string, unknown> | null = null;
  for (const [key, value] of Object.entries(saved)) {
    const visibility = key.startsWith('@');
    const { base, indices } = parseAutocraftIndexedKey(visibility ? key.slice(1) : key);
    const chain = chains.get(base);
    if (!chain || indices.length >= chain.length) {
      continue;
    }
    const padded = [...chain.slice(indices.length).map(() => 0), ...indices];
    const fullKey = `${visibility ? '@' : ''}${autocraftIndexedKey(base, padded)}`;
    if (!(fullKey in saved)) {
      result ??= { ...saved };
      result[fullKey] = value;
    }
  }
  return result ?? saved;
}

function startsWith(indices: readonly number[], prefix: readonly number[]): boolean {
  return prefix.every((value, position) => indices[position] === value);
}

/** Сколько наборов у группы внутри набора предков: `(группа, индексы предков) → число`. */
export type AutocraftSetCounter = (groupKey: string, parentIndices?: readonly number[]) => number;

function setCounterKey(groupKey: string, parentIndices: readonly number[]): string {
  return `${groupKey}\u0000${parentIndices.join(',')}`;
}

/**
 * Счётчик наборов по данным за один проход: каждый ключ разбирается один раз, а не на каждый
 * вопрос «сколько наборов» — тело запроса спрашивает об этом по разу на поле и набор.
 */
export function createAutocraftSetCounter(
  schema: AutocraftGeneratorSchema,
  values: Record<string, unknown>,
): AutocraftSetCounter {
  const maxIndex = new Map<string, number>();
  for (const parsed of parseValueKeys(schema, values)) {
    parsed.chain.forEach((groupKey, depth) => {
      const key = setCounterKey(groupKey, parsed.indices.slice(0, depth));
      maxIndex.set(key, Math.max(maxIndex.get(key) ?? -1, parsed.indices[depth]!));
    });
  }
  return (groupKey, parentIndices = []) =>
    Math.max(1, (maxIndex.get(setCounterKey(groupKey, parentIndices)) ?? -1) + 1);
}

/**
 * Сколько наборов у группы-массива внутри набора предков `parentIndices` — по наибольшему
 * индексу среди её ключей в данных. Ключей нет — один набор: так считает и Автокрафт.
 */
export function countAutocraftArraySets(
  schema: AutocraftGeneratorSchema,
  values: Record<string, unknown>,
  groupKey: string,
  parentIndices: readonly number[] = [],
): number {
  return createAutocraftSetCounter(schema, values)(groupKey, parentIndices);
}

/**
 * Все экземпляры поля: наборы каждой группы-массива его цепочки, от внешней к внутренней.
 * Поле вне массивов — один экземпляр без индексов.
 */
export function listAutocraftFieldInstances(
  schema: AutocraftGeneratorSchema,
  countSets: AutocraftSetCounter,
  key: string,
): number[][] {
  let tuples: number[][] = [[]];
  for (const groupKey of getAutocraftArrayChain(schema, key)) {
    tuples = tuples.flatMap((prefix) =>
      Array.from({ length: countSets(groupKey, prefix) }, (_, index) => [...prefix, index]),
    );
  }
  return tuples;
}

/** Ключи вариантов всех наборов группы-массива внутри набора предков. */
export function listAutocraftArrayVariantKeys(
  countSets: AutocraftSetCounter,
  groupKey: string,
  parentIndices: readonly number[],
): string[] {
  return Array.from({ length: countSets(groupKey, parentIndices) }, (_, index) =>
    autocraftArrayVariantKey(groupKey, [...parentIndices, index]),
  );
}

/**
 * Переставляет наборы группы внутри набора предков: `remap(индекс)` → новый индекс или `null`
 * (набор удаляется). Ключи других групп и наборов не трогаются.
 */
function remapArraySets(
  schema: AutocraftGeneratorSchema,
  values: AutocraftValues,
  groupKey: string,
  parentIndices: readonly number[],
  remap: (index: number) => number | null,
): AutocraftValues {
  const depth = parentIndices.length;
  const affected = new Map<string, ParsedValueKey>();
  for (const parsed of parseValueKeys(schema, values)) {
    if (parsed.chain[depth] === groupKey && startsWith(parsed.indices, parentIndices)) {
      affected.set(parsed.key, parsed);
    }
  }
  const result: AutocraftValues = {};
  for (const [key, value] of Object.entries(values)) {
    const parsed = affected.get(key);
    if (!parsed) {
      result[key] = value;
      continue;
    }
    const nextIndex = remap(parsed.indices[depth]!);
    if (nextIndex == null) {
      continue;
    }
    const indices = [...parsed.indices];
    indices[depth] = nextIndex;
    result[`${parsed.visibility ? '@' : ''}${autocraftIndexedKey(parsed.base, indices)}`] = value;
  }
  return result;
}

/**
 * Ключи нового набора со значениями по умолчанию: поля группы (и вложенных групп), их
 * видимость и варианты. Вложенные массивы нового набора начинаются с одного набора.
 */
function seedArraySet(
  schema: AutocraftGeneratorSchema,
  values: AutocraftValues,
  groupKey: string,
  setIndices: readonly number[],
): AutocraftValues {
  const depth = setIndices.length - 1;
  const result = { ...values };
  const group = fieldOf(schema, groupKey);
  if (group?.isArrayGroup) {
    result[autocraftArrayVariantKey(groupKey, setIndices)] = '';
  }
  for (const key of schemaKeys(schema)) {
    const field = fieldOf(schema, key)!;
    if (field.type === 'link') {
      continue;
    }
    const chain = getAutocraftArrayChain(schema, key);
    if (chain[depth] !== groupKey) {
      continue;
    }
    const indices = [...setIndices, ...chain.slice(depth + 1).map(() => 0)];
    const dataKey = autocraftIndexedKey(autocraftFieldDataBase(key, field), indices);
    result[dataKey] = field.publicDefaultValue ?? '';
    if (field.isDeletable) {
      result[`@${dataKey}`] = field.defaultDrawable !== false;
    }
    if (field.isArrayGroup) {
      result[autocraftArrayVariantKey(key, [...indices, 0])] = '';
    }
  }
  return result;
}

/** Новый пустой набор в конце группы. */
export function addAutocraftArraySet(
  schema: AutocraftGeneratorSchema,
  values: AutocraftValues,
  groupKey: string,
  parentIndices: readonly number[] = [],
): AutocraftValues {
  const count = countAutocraftArraySets(schema, values, groupKey, parentIndices);
  if (count >= AUTOCRAFT_MAX_ARRAY_SETS) {
    return values;
  }
  return seedArraySet(schema, values, groupKey, [...parentIndices, count]);
}

/** Копия набора сразу за ним; наборы после него сдвигаются. */
export function duplicateAutocraftArraySet(
  schema: AutocraftGeneratorSchema,
  values: AutocraftValues,
  groupKey: string,
  parentIndices: readonly number[],
  index: number,
): AutocraftValues {
  if (countAutocraftArraySets(schema, values, groupKey, parentIndices) >= AUTOCRAFT_MAX_ARRAY_SETS) {
    return values;
  }
  const shifted = remapArraySets(schema, values, groupKey, parentIndices, (current) =>
    current > index ? current + 1 : current,
  );
  const depth = parentIndices.length;
  const copies: AutocraftValues = {};
  for (const parsed of parseValueKeys(schema, shifted)) {
    if (
      parsed.chain[depth] === groupKey &&
      startsWith(parsed.indices, parentIndices) &&
      parsed.indices[depth] === index
    ) {
      const indices = [...parsed.indices];
      indices[depth] = index + 1;
      copies[`${parsed.visibility ? '@' : ''}${autocraftIndexedKey(parsed.base, indices)}`] =
        shifted[parsed.key]!;
    }
  }
  return { ...shifted, ...copies };
}

/** Удаляет набор; следующие сдвигаются на его место. Последний набор не удаляется. */
export function removeAutocraftArraySet(
  schema: AutocraftGeneratorSchema,
  values: AutocraftValues,
  groupKey: string,
  parentIndices: readonly number[],
  index: number,
): AutocraftValues {
  if (countAutocraftArraySets(schema, values, groupKey, parentIndices) <= 1) {
    return values;
  }
  return remapArraySets(schema, values, groupKey, parentIndices, (current) => {
    if (current === index) {
      return null;
    }
    return current > index ? current - 1 : current;
  });
}

/** Меняет местами два набора группы (сдвиг вверх/вниз). */
export function swapAutocraftArraySets(
  schema: AutocraftGeneratorSchema,
  values: AutocraftValues,
  groupKey: string,
  parentIndices: readonly number[],
  from: number,
  to: number,
): AutocraftValues {
  const count = countAutocraftArraySets(schema, values, groupKey, parentIndices);
  if (from === to || from < 0 || to < 0 || from >= count || to >= count) {
    return values;
  }
  return remapArraySets(schema, values, groupKey, parentIndices, (current) => {
    if (current === from) {
      return to;
    }
    return current === to ? from : current;
  });
}
